import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import BarWithImageChartComponent from "../../../components/CustomCharts/BarChart/BarWithImageChartComponent";
import CustomAreaChartComponent from "../../../components/CustomCharts/AreaCharts/CustomAreaCharts";
import CustomBarChartComponent from "../../../components/CustomCharts/BarChart/CustomBarChartComponent";
import FunnelChart from "../../../components/CustomCharts/AreaCharts/FunnelChart";
import GBPTable from "../../../Pages/businessDashboard/GBPTable";
import GSCTable from "../../../Pages/businessDashboard/GSCTable";
import LazyLoad from "react-lazyload";
import Loader from "../../../utils/loader/Loader";
import React from "react";
import StatsCard from "../../../Pages/businessDashboard/StatsCards";
import StatsCardWithStars from "../../../Pages/businessDashboard/StatsCardWithStars";

interface TrendDetail {
  Total: string;
  Diff: string;
  Trend: string;
}

interface TrendData {
  [key: string]: TrendDetail;
}

interface DashboardProps {
  organicRef: React.RefObject<HTMLDivElement>;
  webRef: React.RefObject<HTMLDivElement>;
  aiRef: React.RefObject<HTMLDivElement>;
  ChartData: any[];
  ChartKey: any[];
  trendData: TrendData;
  queryContentGroupsData: any[];
  contentData: any[];
  queriesData: any[];
  queryClustersData: any[];
  setSelectedTab: (tab: number) => void;
  screenHeight: any;
  loading: boolean;
  goals: any[];
  ga4Data: any[];
  ga4Date: any[];
  memoizedGACardData: any;
  ga4BarData: any[];
  ga4BarDate: any[];
  sessionSource: any[];
  landingPageContentData: any[];
  pagePath: any[];
  location: string;
  handleChange: any;
  profileData: any;
  interactions: any;
  interActionTrendData: any;
  localPresenceQueries: any[];
  localRef: React.RefObject<HTMLDivElement>;
  funnelData: any;
  aiTrendData: any;
  aiBarData: any;
  aiLineData: any;
  reviewsData: any;
  reviewsRef: React.RefObject<HTMLDivElement>;
  month: any;
  topQueriesCards: any;
  topQueries: any;
  chartReady: any;
}

const Dashboard: React.FC<DashboardProps> = ({
  organicRef,
  webRef,
  aiRef,
  ChartData,
  ChartKey,
  trendData,
  queryContentGroupsData,
  contentData,
  queriesData,
  queryClustersData,
  setSelectedTab,
  screenHeight,
  loading,
  goals,
  ga4Data,
  ga4Date,
  memoizedGACardData,
  ga4BarData,
  ga4BarDate,
  sessionSource,
  landingPageContentData,
  pagePath,
  location,
  handleChange,
  profileData,
  interactions,
  interActionTrendData,
  localPresenceQueries,
  localRef,
  funnelData,
  aiTrendData,
  aiBarData,
  aiLineData,
  reviewsData,
  reviewsRef,
  month,
  topQueriesCards,
  topQueries,
  chartReady
}) => {
  const modelConfig = [
    {
      name: "OpenAI",
      match: "chatgpt",
      logo: "/icons/aimodels/openai.svg",
      color: "#000000",
    },
    {
      name: "Claude",
      match: "claude",
      logo: "/icons/aimodels/claude.png",
      color: "#F9E3DC",
    },
    {
      name: "perplexity",
      match: "perplexity",
      logo: "/icons/aimodels/perplexity.svg",
      color: "#3B9DBD",
    },
    {
      name: "Gemini",
      match: "gemini",
      logo: "/icons/aimodels/gemini.svg",
      color: "",
    },
    {
      name: "Bing",
      match: "bing",
      logo: "/icons/aimodels/bing.png",
      color: "#D9EAF7",
    },
  ];

  const finalAiBarData = modelConfig.map((model) => {
    const match = aiBarData.find(
      (item: any) => item.Source.toLowerCase() === model.match
    );

    return {
      name: model.name,
      logo: model.logo,
      value: match ? parseInt(match.Total, 10) : 0,
      color: model.color,
      diff: match ? parseInt(match.Diff.replace("%", ""), 10) : 0,
      trend: match ? match.Trend : "down",
    };
  });
  // Calculate the maximum value for percentage calculation
  const aiModalMaxValue = Math.max(...finalAiBarData.map((d: any) => d.value));

  const aiModalSeries = [
    {
      data: finalAiBarData.map((d: any) => d.value),
    },
  ];

  return (
    <>
      <Loader spinning={loading}>
        <LazyLoad height={screenHeight / 2} offset={screenHeight / 4}>
          <Box className="gsc-container-v2" ref={organicRef}>
            <Box className="gsc-title-v2">
              <Typography
                variant="h5"
                sx={{
                  color: "#FFF",
                  fontFeatureSettings: "'liga' off, 'clig' off",
                }}
              >
                Organic Performance
              </Typography>
            </Box>

            <Box className="gsc-tools-v2">
              <Box className="gsc-first-row-v2">
                <Box
                  className="gsc-line-chart-v2"
                  sx={{ minWidth: 704, height: 380, width: "70%" }}
                >
                  {ChartData.length > 0 &&
                    ChartKey.length > 0 &&
                    ga4Data.length > 0 &&
                    memoizedGACardData.keyEvents && (
                      <CustomAreaChartComponent
                        key={JSON.stringify(ChartKey)}
                        loading={loading}
                        chartType="area"
                        colors={[
                          { clk: "#4285F4" },
                          { imp: "#5E35B1" },
                          { goals: "rgba(0, 137, 123, 0.60)" },
                        ]}
                        categories={ChartKey}
                        overViews={trendData}
                        seriesData={ChartData}
                        title="Custom Chart Title"
                        condition={false}
                      />
                    )}
                </Box>
                <Box className="gsc-funnel-chart-v2">
                  {funnelData !== null &&
                    funnelData !== undefined &&
                    Object.keys(funnelData).length > 0 &&
                    funnelData.clicks &&
                    funnelData.impressions &&
                    funnelData.cnv && <FunnelChart response={funnelData} />}
                </Box>
              </Box>
              <Box className="gsc-second-row-v2">
                <GSCTable
                  title="Content Groups"
                  data={queryContentGroupsData}
                  colSpan={5}
                  message="Missing Content Groups"
                  linkText="a content group to activate this widget."
                  onLinkClick={() => {
                    setSelectedTab(5);
                  }}
                />
                <Box sx={{ width: "50%" }}>
                  <GSCTable
                    title="Top Pages"
                    data={contentData}
                    colSpan={5}
                    linkText="No data available"
                  />
                </Box>
              </Box>
              <Box className="gsc-third-row-v2">
                <GSCTable
                  title="Top Queries"
                  data={queriesData}
                  colSpan={5}
                  linkText="No data available"
                />
                <Box
                  className="gsc-line-chart-v2"
                  sx={{
                    width: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 300,
                  }}
                >
                  {!chartReady ? (
                    <CircularProgress />
                  ) : (
                    Object.keys(topQueries).length > 0 &&
                    Object.keys(topQueriesCards).length > 0 && (
                      <CustomAreaChartComponent
                        key={JSON.stringify(topQueries?.keys)}
                        loading={loading}
                        chartType="area"
                        isCustomPadding={true}
                        colors={[
                          { pos1_3: "rgba(0, 137, 123, 0.60)" },
                          { pos4_10: "#4285F4" },
                          { pos11_20: "rgba(66, 133, 244, 0.8)" },
                          { pos20_plus: "rgba(66, 133, 244, 0.5)" },
                        ]}
                        categories={topQueries?.keys}
                        overViews={topQueriesCards}
                        seriesData={topQueries?.data}
                        title="Custom Chart Title"
                        condition={false}
                      />
                    )
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            className="gbp-container-v2"
            ref={webRef}
            sx={{ marginTop: "48px" }}
          >
            <Box className="gbp-title-v2">
              <Typography
                variant="h5"
                sx={{
                  color: "#FFF",
                  fontFeatureSettings: "'liga' off, 'clig' off",
                }}
              >
                Web Performance
              </Typography>
            </Box>
            {/* <Box className="line" /> */}
            <Box className="gbp-tools-v2">
              <Box className="gbp-first-row-v2">
                {goals !== null && goals !== undefined && goals.length > 0 && (
                  <GBPTable
                    title="Goals"
                    data={goals}
                    colSpan={5}
                    linkText="No data available."
                    condition={false}
                  />
                )}
                <Box
                  className="gbp-line-chart-v2"
                  sx={{ minWidth: 704, height: 380, width: "70%" }}
                >
                  {ga4Data.length > 0 && ga4Date.length > 0 && (
                    <CustomAreaChartComponent
                      key={JSON.stringify(ga4Data)}
                      chartType="area"
                      colors={[
                        { sessions: "rgba(66, 133, 244, 0.60)" },
                        { keyevents: "rgba(0, 137, 123, 0.60)" },
                      ]}
                      categories={ga4Date}
                      overViews={memoizedGACardData}
                      seriesData={ga4Data}
                      condition={false}
                    />
                  )}
                </Box>
              </Box>
              <Box className="gbp-second-row-v2">
                {sessionSource !== null &&
                  sessionSource !== undefined &&
                  sessionSource.length > 0 && (
                    <GBPTable
                      title="Top Sources"
                      data={sessionSource}
                      colSpan={5}
                      linkText="No data available."
                      condition={true}
                    />
                  )}
                <Box
                  className="gbp-line-chart-v2"
                  sx={{ minWidth: 520, height: 380, width: "50%" }}
                >
                  {ga4BarData?.length > 0 && ga4BarData?.length > 0 && (
                    <CustomBarChartComponent
                      series={ga4BarData}
                      categories={ga4BarDate}
                    />
                  )}
                </Box>
              </Box>
              <Box className="gbp-second-row-v2">
                <GBPTable
                  title="Content Groups"
                  data={landingPageContentData}
                  colSpan={5}
                  message="Missing Content Groups"
                  linkText="a content group to activate this widget."
                  onLinkClick={() => {
                    setSelectedTab(5);
                  }}
                  condition={true}
                />
                <Box sx={{ width: "50%" }}>
                  <GBPTable
                    title="Top Pages"
                    data={pagePath}
                    colSpan={5}
                    linkText="No data available."
                    condition={true}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            className="ai-container-v2"
            ref={aiRef}
            sx={{ marginTop: "48px" }}
          >
            <Box className="ai-title-v2">
              <Typography
                variant="h5"
                sx={{
                  color: "#FFF",
                  fontFeatureSettings: "'liga' off, 'clig' off",
                }}
              >
                AI Search
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                columnGap: "16px",
                alignItems: "stretch",
                width: "100%",
              }}
            >
              <Box
                className="ai-line-chart-v2"
                sx={{ minWidth: 520, height: 380, flex: 1, width: "50%" }}
              >
                {Object.keys(aiLineData).length > 0 && (
                  <CustomAreaChartComponent
                    key={JSON.stringify(aiLineData.keys)}
                    chartType="area"
                    colors={[{ aitraffic: "#BA68C8" }]}
                    customGradient={["#FF15AA", "#4285F4", "#38D2D40D"]}
                    categories={aiLineData.keys}
                    isCheckbox={false}
                    isCustomPadding={true}
                    overViews={aiTrendData}
                    seriesData={aiLineData.data}
                    title="Custom Chart Title"
                    condition={true}
                  />
                )}
              </Box>
              <Box
                className="ai-line-chart-v2"
                padding={2}
                sx={{
                  bgcolor: "#f6f8f8",
                  borderRadius: "8px",
                  width: "50%",
                }}
              >
                <Box>
                  <Typography variant="body1" color="text.secondary" mb={2}>
                    Showing relative search traffic across major LLMs
                  </Typography>
                  <Box sx={{ paddingLeft: 7 }}>
                    {finalAiBarData.length > 0 && (
                      <BarWithImageChartComponent
                        series={aiModalSeries}
                        data={finalAiBarData}
                        maxValue={aiModalMaxValue}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {false && (
            <>
              <Box
                className="location-container-v2"
                ref={localRef}
                sx={{ marginTop: "48px" }}
              >
                <Box className="location-title-v2">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row !important",
                      columnGap: "24px",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: "#FFF",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                      }}
                    >
                      Local Search
                    </Typography>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={location}
                      onChange={handleChange}
                      sx={{
                        color: "#fff",
                        height: "40px",
                        outline: "none",
                        background: "rgba(255, 255, 255, 0.10)",
                      }}
                    >
                      {profileData.Locations !== undefined &&
                        profileData.Locations.map((loc: any, index: number) => (
                          <MenuItem key={loc.Name} value={loc.Name}>
                            {loc.DisplayName}- {index + 1}
                          </MenuItem>
                        ))}
                    </Select>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFF",
                      fontFeatureSettings: "'liga' off, 'clig' off",
                    }}
                  >
                    Source: Google Business Profile
                  </Typography>
                </Box>
                <Box className="line" />
                <Box className="location-tools-v2">
                  <Box className="location-first-row-v2">
                    <Box
                      className="location-line-chart-v2"
                      sx={{ minWidth: 520, height: 380 }}
                    >
                      {Object.keys(interactions).length > 0 && (
                        <CustomAreaChartComponent
                          key={JSON.stringify(interactions.keys)}
                          loading={loading}
                          chartType="area"
                          colors={[{ interactions: "#007bff" }]}
                          isCheckbox={false}
                          categories={interactions.keys}
                          overViews={interActionTrendData}
                          seriesData={interactions.data}
                          title="Custom Chart Title"
                          condition={true}
                        />
                      )}
                    </Box>
                    <GBPTable
                      title="Top Queries"
                      data={localPresenceQueries}
                      colSpan={5}
                      linkText="No data available"
                      location={true}
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                className="reviews-container-v2"
                ref={reviewsRef}
                sx={{ marginTop: "48px" }}
              >
                <Box className="reviews-title-v2">
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#FFF",
                      fontFeatureSettings: "'liga' off, 'clig' off",
                    }}
                  >
                    Reviews
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFF",
                      fontFeatureSettings: "'liga' off, 'clig' off",
                    }}
                  >
                    Location: {location}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    columnGap: "16px",
                    alignItems: "stretch",
                    width: "100%",
                  }}
                >
                  <StatsCardWithStars
                    title={`Ratings Past ${month}`}
                    description="Average rating"
                    value={reviewsData?.ratingspast?.Total || "0"}
                    percentage={
                      `${parseFloat(reviewsData?.ratingspast?.Diff).toFixed(
                        1
                      )}%` || "0%"
                    }
                    increase={
                      reviewsData?.ratingspast?.Diff === "up" ? true : false
                    }
                  />
                  <StatsCard
                    title="All Time Reviews"
                    description=""
                    value={reviewsData?.alltimereviews?.Total || "0"}
                    percentage="0%"
                    increase={
                      reviewsData?.alltimereviews?.Diff === "up" ? true : false
                    }
                    con={true}
                  />
                </Box>
              </Box>{" "}
            </>
          )}
        </LazyLoad>
      </Loader>
    </>
  );
};

export default Dashboard;
